/**
 * QuestSystem — Manages quest lifecycle: accepting, tracking, completing, and failing quests.
 * All quest definitions are loaded from src/data/quests.json.
 * Rewards are applied through WorldState (gold/fame) and optionally ItemSystem
 * (items — loaded dynamically to avoid hard dependency).
 *
 * @module QuestSystem
 */

import DebugLog from '../debug/DebugLog.js';
import EventBus from '../core/EventBus.js';
import WorldState from '../world/WorldState.js';
import FameSystem from './FameSystem.js';
import QUESTS_DATA from '../data/quests.json';

// ─── QuestSystem ──────────────────────────────────────────────────────────────

const QuestSystem = {
  /**
   * Active quest instances with per-objective progress tracking.
   * @type {Array<object>}
   */
  _active: [],

  /**
   * IDs of quests that have been completed.
   * @type {string[]}
   */
  _completed: [],

  /**
   * IDs of quests that have been failed.
   * @type {string[]}
   */
  _failed: [],

  /**
   * All quest templates, indexed by id for O(1) lookup.
   * @type {Map<string, object>}
   */
  _templates: new Map(QUESTS_DATA.map(q => [q.id, q])),

  // ─── Core API ──────────────────────────────────────────────────────────────

  /**
   * Accept a quest by id. Creates an active instance with fresh progress state.
   * @param {string} questId
   * @returns {{ success: boolean, message: string }}
   */
  acceptQuest(questId) {
    const check = this.canAccept(questId);
    if (!check.canAccept) {
      DebugLog.warn(`[quest] acceptQuest failed; id=${questId} reason=${check.reason}`);
      return { success: false, message: check.reason };
    }

    const template = this._templates.get(questId);

    const instance = {
      id: questId,
      title: template.title,
      type: template.type,
      description: template.description,
      questGiver: template.questGiver ?? null,
      acceptedAt: { ...WorldState.time },
      objectives: template.objectives.map(obj => ({
        ...obj,
        progress: 0,
        completed: false,
      })),
      rewards: template.rewards ?? [],
    };

    this._active.push(instance);

    DebugLog.info(`[quest] Quest accepted; id=${questId} title="${template.title}"`);
    EventBus.emit('quest:accepted', { questId, title: template.title, instance });

    return { success: true, message: `Quest accepted: ${template.title}` };
  },

  /**
   * Update objective progress across all active quests that match the type+targetId.
   * Automatically marks objectives complete when count is reached.
   * @param {string} type — 'kill' | 'collect' | 'deliver' | 'reach' | 'talk'
   * @param {string} targetId
   * @param {number} [amount]
   */
  updateObjective(type, targetId, amount = 1) {
    if (!targetId) return;

    let anyUpdated = false;

    for (const instance of this._active) {
      for (const obj of instance.objectives) {
        if (obj.completed) continue;
        if (obj.type !== type) continue;
        if (obj.targetId !== targetId) continue;

        const before = obj.progress;
        obj.progress = Math.min(obj.count, obj.progress + amount);

        if (obj.progress !== before) {
          anyUpdated = true;

          // Update label to reflect current progress
          obj.label = obj.label.replace(
            /\(\d+\/\d+\)$/,
            `(${obj.progress}/${obj.count})`
          );

          if (obj.progress >= obj.count) {
            obj.completed = true;
            DebugLog.event(
              `[quest] Objective completed; questId=${instance.id} ` +
              `objectiveId=${obj.id} type=${type} targetId=${targetId}`
            );
            EventBus.emit('quest:objectiveComplete', {
              questId: instance.id,
              objectiveId: obj.id,
            });
          } else {
            DebugLog.event(
              `[quest] Objective progress; questId=${instance.id} ` +
              `objectiveId=${obj.id} progress=${obj.progress}/${obj.count}`
            );
            EventBus.emit('quest:progress', {
              questId: instance.id,
              objectiveId: obj.id,
              progress: obj.progress,
              count: obj.count,
            });
          }
        }
      }

      // Auto-complete if all objectives are done
      if (anyUpdated && instance.objectives.every(o => o.completed)) {
        DebugLog.info(`[quest] All objectives complete, auto-completing; id=${instance.id}`);
        // Defer slightly so current call stack finishes cleanly
        const idToComplete = instance.id;
        Promise.resolve().then(() => this.completeQuest(idToComplete));
      }
    }
  },

  /**
   * Complete a quest. Applies all rewards and moves it from active to completed.
   * Emits 'quest:completed'.
   * @param {string} questId
   * @returns {{ success: boolean, rewards: object[], message: string }}
   */
  completeQuest(questId) {
    const instance = this.getById(questId);
    if (!instance) {
      // Also allow completing a quest that was never formally accepted (debug)
      const template = this._templates.get(questId);
      if (!template) {
        return { success: false, rewards: [], message: `Unknown quest: ${questId}` };
      }
      // Accept it first silently, then complete
      this.acceptQuest(questId);
      return this.completeQuest(questId);
    }

    // Remove from active
    this._active = this._active.filter(q => q.id !== questId);

    // Mark completed
    if (!this._completed.includes(questId)) {
      this._completed.push(questId);
    }

    // Apply rewards
    const appliedRewards = this._applyRewards(instance.rewards, questId);

    DebugLog.info(
      `[quest] Quest completed; id=${questId} title="${instance.title}" ` +
      `rewards=${JSON.stringify(appliedRewards)}`
    );

    EventBus.emit('quest:completed', {
      questId,
      title: instance.title,
      rewards: appliedRewards,
    });

    return {
      success: true,
      rewards: appliedRewards,
      message: `Quest complete: ${instance.title}`,
    };
  },

  /**
   * Fail a quest. Applies a fame penalty proportional to the quest's fame reward.
   * Moves quest from active to failed. Emits 'quest:failed'.
   * @param {string} questId
   */
  failQuest(questId) {
    const instance = this.getById(questId);
    if (!instance) {
      const template = this._templates.get(questId);
      if (!template) {
        DebugLog.warn(`[quest] failQuest: unknown quest id=${questId}`);
        return { success: false, message: `Unknown quest: ${questId}` };
      }
      // Quest exists but was never accepted — just mark failed silently
      if (!this._failed.includes(questId)) {
        this._failed.push(questId);
      }
      return { success: true, message: `Quest marked failed: ${template.title}` };
    }

    // Remove from active
    this._active = this._active.filter(q => q.id !== questId);

    if (!this._failed.includes(questId)) {
      this._failed.push(questId);
    }

    // Fame penalty: half of the fame reward this quest would have given
    const fameReward = instance.rewards.find(r => r.type === 'fame');
    const famePenalty = fameReward ? -Math.ceil(fameReward.amount * 0.5) : -10;
    FameSystem.addFame(famePenalty);

    DebugLog.event(
      `[quest] Quest failed; id=${questId} title="${instance.title}" ` +
      `famePenalty=${famePenalty}`
    );

    EventBus.emit('quest:failed', {
      questId,
      title: instance.title,
      famePenalty,
    });

    return { success: true, message: `Quest failed: ${instance.title}` };
  },

  /**
   * Get all active quest instances with current progress state.
   * @returns {Array<object>}
   */
  getActive() {
    return this._active;
  },

  /**
   * Get an active quest instance by id.
   * @param {string} questId
   * @returns {object|null}
   */
  getById(questId) {
    return this._active.find(q => q.id === questId) ?? null;
  },

  /**
   * Get all quest templates available at a specific location.
   * Filters by: fame requirement met, not already active/completed/failed,
   * and region level within quest's min/max range.
   * @param {string} locationId — town id or region id
   * @param {number} [regionLevel]
   * @returns {Array<object>} Array of quest templates
   */
  getAvailableAt(locationId, regionLevel) {
    const currentFame = FameSystem.getFame();
    const level = regionLevel ?? WorldState.currentRegionLevel ?? 1;

    return QUESTS_DATA.filter(template => {
      // Fame gate
      if (currentFame < (template.requiredFame ?? -1000)) return false;

      // Region level range
      if (level < (template.minRegionLevel ?? 1)) return false;
      if (level > (template.maxRegionLevel ?? 10)) return false;

      // Not already active, completed, or failed
      if (this._active.some(q => q.id === template.id)) return false;
      if (this._completed.includes(template.id)) return false;
      if (this._failed.includes(template.id)) return false;

      // Prerequisites check
      if (template.prerequisites?.length) {
        for (const prereq of template.prerequisites) {
          if (!this._completed.includes(prereq)) return false;
        }
      }

      // Location filter
      const availableAt = template.availableAt ?? ['any_town'];
      if (availableAt.includes('any_town')) return true;
      if (availableAt.includes('start') && locationId === 'start') return true;
      if (availableAt.includes(locationId)) return true;

      return false;
    });
  },

  /**
   * Check whether the party can accept a quest.
   * @param {string} questId
   * @returns {{ canAccept: boolean, reason: string }}
   */
  canAccept(questId) {
    const template = this._templates.get(questId);
    if (!template) {
      return { canAccept: false, reason: `Unknown quest: ${questId}` };
    }

    if (this._active.some(q => q.id === questId)) {
      return { canAccept: false, reason: 'Quest is already active.' };
    }

    if (this._completed.includes(questId)) {
      return { canAccept: false, reason: 'Quest has already been completed.' };
    }

    if (this._failed.includes(questId)) {
      return { canAccept: false, reason: 'Quest was previously failed and cannot be retried.' };
    }

    const currentFame = FameSystem.getFame();
    if (currentFame < (template.requiredFame ?? -1000)) {
      return {
        canAccept: false,
        reason: `Requires fame ${template.requiredFame} (current: ${currentFame}).`,
      };
    }

    // Prerequisite check
    if (template.prerequisites?.length) {
      for (const prereq of template.prerequisites) {
        if (!this._completed.includes(prereq)) {
          const prereqTemplate = this._templates.get(prereq);
          const prereqTitle = prereqTemplate?.title ?? prereq;
          return {
            canAccept: false,
            reason: `Requires completion of: ${prereqTitle}`,
          };
        }
      }
    }

    return { canAccept: true, reason: null };
  },

  /**
   * Get all quest templates (for debug / quest board display).
   * @returns {object[]}
   */
  getAllTemplates() {
    return QUESTS_DATA;
  },

  // ─── Serialization ─────────────────────────────────────────────────────────

  /**
   * Serialize quest state for the save system.
   * @returns {{ active: object[], completed: string[], failed: string[] }}
   */
  serialize() {
    return {
      active: JSON.parse(JSON.stringify(this._active)),
      completed: [...this._completed],
      failed: [...this._failed],
    };
  },

  /**
   * Restore quest state from save data.
   * @param {{ active: object[], completed: string[], failed: string[] }} data
   */
  deserialize(data) {
    if (!data) return;
    this._active = Array.isArray(data.active) ? data.active : [];
    this._completed = Array.isArray(data.completed) ? data.completed : [];
    this._failed = Array.isArray(data.failed) ? data.failed : [];

    DebugLog.info(
      `[quest] QuestSystem deserialized; active=${this._active.length} ` +
      `completed=${this._completed.length} failed=${this._failed.length}`
    );
  },

  /**
   * Reset all quest state (used on new game).
   */
  reset() {
    this._active = [];
    this._completed = [];
    this._failed = [];
    DebugLog.info('[quest] QuestSystem reset');
  },

  // ─── Internal helpers ──────────────────────────────────────────────────────

  /**
   * Apply a reward list. Gold and fame are applied immediately.
   * Item rewards attempt a dynamic import of ItemSystem; if unavailable they
   * are logged but not applied (will be wired once ItemSystem is loaded).
   * @param {object[]} rewards
   * @param {string} questId — for logging context
   * @returns {object[]} Applied reward descriptors
   */
  _applyRewards(rewards, questId) {
    if (!Array.isArray(rewards) || rewards.length === 0) return [];

    const applied = [];

    for (const reward of rewards) {
      switch (reward.type) {
        case 'gold': {
          const amount = reward.amount ?? 0;
          WorldState.partyGold = (WorldState.partyGold ?? 0) + amount;
          applied.push({ type: 'gold', amount });
          DebugLog.event(`[quest] Reward applied; questId=${questId} type=gold amount=${amount}`);
          break;
        }

        case 'fame': {
          const amount = reward.amount ?? 0;
          FameSystem.addFame(amount);
          applied.push({ type: 'fame', amount });
          DebugLog.event(`[quest] Reward applied; questId=${questId} type=fame amount=${amount}`);
          break;
        }

        case 'item': {
          const { itemId, qty = 1 } = reward;
          applied.push({ type: 'item', itemId, qty });
          // Attempt to give items via ItemSystem (dynamic import; fail silently)
          this._giveItemReward(itemId, qty, questId);
          break;
        }

        default:
          DebugLog.warn(`[quest] Unknown reward type="${reward.type}" in quest ${questId}`);
          break;
      }
    }

    return applied;
  },

  /**
   * Attempt to give item rewards via ItemSystem.
   * Uses dynamic import so QuestSystem works standalone before ItemSystem is loaded.
   * Falls back to logging the item grant so it can be applied manually.
   * @param {string} itemId
   * @param {number} qty
   * @param {string} questId
   */
  async _giveItemReward(itemId, qty, questId) {
    try {
      const { default: ItemSystem } = await import('./ItemSystem.js');
      // Give to the first hero character in the party
      const { default: CharacterSystem } = await import('./CharacterSystem.js');
      const heroes = CharacterSystem.getAll().filter(c => c.isHero);
      if (heroes.length > 0) {
        ItemSystem.giveItem(heroes[0], itemId, qty);
        DebugLog.event(
          `[quest] Item reward given; questId=${questId} itemId=${itemId} ` +
          `qty=${qty} charId=${heroes[0].id}`
        );
      } else {
        DebugLog.warn(
          `[quest] Item reward pending (no hero found); questId=${questId} ` +
          `itemId=${itemId} qty=${qty}`
        );
        EventBus.emit('quest:itemRewardPending', { questId, itemId, qty });
      }
    } catch (_err) {
      // ItemSystem not yet loaded — emit pending event so UI can handle it
      DebugLog.warn(
        `[quest] ItemSystem unavailable, item reward pending; ` +
        `questId=${questId} itemId=${itemId} qty=${qty}`
      );
      EventBus.emit('quest:itemRewardPending', { questId, itemId, qty });
    }
  },
};

// ─── Sync WorldState quest arrays ─────────────────────────────────────────────

// Keep WorldState.activeQuests and WorldState.completedQuests in sync
// so the save system (which reads WorldState) sees current data.
EventBus.on('quest:accepted', () => {
  WorldState.activeQuests = QuestSystem._active;
});

EventBus.on('quest:completed', () => {
  WorldState.activeQuests = QuestSystem._active;
  WorldState.completedQuests = QuestSystem._completed;
});

EventBus.on('quest:failed', () => {
  WorldState.activeQuests = QuestSystem._active;
});

export default QuestSystem;
